/**
 * Fuso horario e formatacao de datas do sistema.
 *
 * Todo o armazenamento e feito em UTC (colunas `timestamptz`), e a
 * apresentacao/entrada usa o fuso oficial do sistema (America/Sao_Paulo).
 * Isso evita que um encontro noturno "vire o dia" na interface.
 */

export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

let timezone = DEFAULT_TIMEZONE;

export function setTimezone(value: string): void {
  try {
    new Intl.DateTimeFormat('pt-BR', { timeZone: value }).format(new Date());
    timezone = value;
  } catch {
    timezone = DEFAULT_TIMEZONE;
  }
}

export function getTimezone(): string {
  return timezone;
}

/* ---------- Formatacao ------------------------------------------------ */

const dateFormatter = (): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: timezone,
  });

const timeFormatter = (): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  });

const weekdayFormatter = (): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat('pt-BR', { weekday: 'long', timeZone: timezone });

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter().format(date);
}

/** Hora no padrao 24 horas, no fuso do sistema. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : timeFormatter().format(date);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${formatDate(iso)} ${formatTime(iso)}`;
}

export function formatWeekday(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const label = weekdayFormatter().format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Data no formato YYYY-MM-DD (fuso do sistema), para agrupar por dia. */
export function formatIsoDate(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }).format(date);
  return parts;
}

/**
 * Converte um valor de `<input type="datetime-local">` (que representa
 * hora local do usuario) para um instante em UTC considerando o fuso do
 * sistema. Formato aceito: "YYYY-MM-DDTHH:mm".
 */
export function localInputToIso(value: string, timeZone = timezone): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback.toISOString();
  }

  const [, year, month, day, hour, minute] = match;
  const utcGuess = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );

  // Descobre o deslocamento do fuso naquele instante (respeita horario de verao).
  const offset = timezoneOffsetMs(new Date(utcGuess), timeZone);
  return new Date(utcGuess - offset).toISOString();
}

/** Converte um instante UTC para "YYYY-MM-DDTHH:mm" no fuso do sistema. */
export function isoToLocalInput(iso: string | null | undefined, timeZone = timezone): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  }).format(date);

  return parts.replace(' ', 'T').slice(0, 16);
}

/** Somente a data (YYYY-MM-DD) de um instante, no fuso do sistema. */
export function isoToLocalDate(iso: string | null | undefined, timeZone = timezone): string {
  const value = isoToLocalInput(iso, timeZone);
  return value.slice(0, 10);
}

/** Deslocamento do fuso (em ms) para o instante informado. */
function timezoneOffsetMs(date: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const read = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  const asUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second'),
  );

  return asUtc - date.getTime();
}

/** Soma dias a uma data mantendo o horario local do fuso do sistema. */
export function addDaysLocal(iso: string, days: number, timeZone = timezone): string {
  const base = isoToLocalInput(iso, timeZone);
  if (!base) return iso;
  const [datePart, timePart] = base.split('T');
  const [year, month, day] = (datePart ?? '').split('-').map(Number);
  const shifted = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
  const nextDate = `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(
    shifted.getUTCDate(),
  ).padStart(2, '0')}`;
  return localInputToIso(`${nextDate}T${timePart ?? '00:00'}`, timeZone) ?? iso;
}
